import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@repo/components';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getCompanyEventLogs } from 'src/api/company';
import LogsTab from 'src/parts/tabs/LogsTab';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { constructQuery } from '@repo/shared/helpers';
import { startOfDay, subMonths, endOfDay } from 'date-fns';
interface LogsSearch {
	from?: string;
	until?: string;
	page?: number;
	sort?: string;
}

export const Route = createFileRoute('/company_/$groupId/events/$eventId/logs')({
	validateSearch: (search: Record<string, unknown>): LogsSearch => {
		const now = new Date();
		return {
			from: (search.from as string) || startOfDay(subMonths(now, 1)).toISOString(),
			until: (search.until as string) || endOfDay(now).toISOString(),
			page: (search.page as number) || undefined,
			sort: (search.sort as string) || undefined,
		};
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { groupId, eventId } = Route.useParams();
	const { from, until, page, sort } = Route.useSearch();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const queryClient = useQueryClient();

	const query = constructQuery({
		type: 'EventLog',
		from,
		until,
		page,
		sort: sort || 'timestamp:desc',
	});

	const { data: logs, isLoading: logsLoading } = useQuery({
		queryKey: ['company-event-logs', groupId, eventId, from, until, page, sort],
		queryFn: () => getCompanyEventLogs(groupId, eventId, query),
		enabled: !userLoading,
	});

	useBreadcrumbLevel(3, 'Logs');

	if (userLoading || logsLoading) {
		return <Spinner className="text-[#1E1E1E]" />;
	}

	if (!authenticatedUser) return null;

	const handleReload = () => {
		queryClient.invalidateQueries({ queryKey: ['company-event-logs', groupId, eventId] });
	};

	return <LogsTab showFilter={false} logType="EventLog" logs={logs} onReload={handleReload} />;
}
